import { appInsights } from '@/components/azureAppInsights/AzureAppInsights';

const AUTH_SESSION_PATH = '/api/auth/session';
const PATCH_FLAG = '__stylusAuthFetchDiagnosticsInstalled';

type WindowWithAuthFetchDiagnostics = Window &
  typeof globalThis & {
    [PATCH_FLAG]?: boolean;
  };

function isAuthSessionUrl(input: Parameters<typeof fetch>[0]) {
  const url = typeof input === 'string' ? input : input instanceof URL ? input.href : input.url;

  try {
    return new URL(url, window.location.origin).pathname === AUTH_SESSION_PATH;
  } catch {
    return url.includes(AUTH_SESSION_PATH);
  }
}

function tryParseJson(text: string) {
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

function stringifyValue(value: unknown) {
  if (typeof value === 'string') return value;

  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
}

function trackAuthSessionFetchFailure(properties: Record<string, unknown>) {
  try {
    appInsights.trackException({
      exception: new Error('NextAuth session fetch failed'),
      properties: Object.fromEntries(
        Object.entries(properties).map(([key, value]) => [key, stringifyValue(value)]),
      ),
    });
  } catch (telemetryError) {
    console.error('[next-auth][session][app insights failed]', telemetryError);
  }
}

if (typeof window !== 'undefined') {
  const target = window as WindowWithAuthFetchDiagnostics;

  if (!target[PATCH_FLAG]) {
    target[PATCH_FLAG] = true;

    const originalFetch = window.fetch.bind(window);

    window.fetch = async (...args) => {
      const isAuthSessionRequest = isAuthSessionUrl(args[0]);

      try {
        const response = await originalFetch(...args);

        if (isAuthSessionRequest && !response.ok) {
          const responseText = await response.clone().text();
          const diagnostics = {
            status: response.status,
            statusText: response.statusText,
            url: response.url || AUTH_SESSION_PATH,
            response: responseText ? tryParseJson(responseText) : null,
          };

          console.error('[next-auth][session][fetch failed]', diagnostics);
          trackAuthSessionFetchFailure(diagnostics);
        }

        return response;
      } catch (error) {
        if (isAuthSessionRequest) {
          console.error('[next-auth][session][fetch threw]', error);
          trackAuthSessionFetchFailure({
            url: AUTH_SESSION_PATH,
            error,
          });
        }

        throw error;
      }
    };
  }
}
